import { newTracingSheet } from "./tracing-sheet";
import { OpacityControl } from "./TraceControls";
import { paperRemoval } from "./WhitePaperFilter";
import { useEffect, useRef, useState } from "react";
import { AssetRecordType, type TLImageShape } from "@tldraw/tlschema";
import type { WorkspaceModel } from "./useWorkspace";
import { notify, uploadFile } from "./api";
export function ImageControls({
  model,
  shape: s,
}: {
  model: WorkspaceModel;
  shape: TLImageShape;
}) {
  const { editor } = model;
  const [busy, setBusy] = useState(false),
    asset = s.props.assetId && editor.getAsset(s.props.assetId);
  const [pending, setPending] = useState<File | null>(null);
  const [preview, setPreview] = useState("");
  const [placement, setPlacement] = useState<"fit" | "fill">("fill");
  const update = (props: Partial<TLImageShape["props"]>) =>
    editor.updateShape<TLImageShape>({ id: s.id, type: "image", props });
  const style = (key: string, value: string | number) =>
    editor.updateShape({
      id: s.id,
      type: "image",
      meta: { ...s.meta, [key]: value },
    });
  const replacement = useRef<AbortController | null>(null);
  useEffect(() => () => replacement.current?.abort(), []);
  useEffect(() => {
    if (!pending) {
      setPreview("");
      return;
    }
    const url = URL.createObjectURL(pending);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [pending]);
  const applyReplacement = async () => {
    if (!pending || busy) return;
    setBusy(true);
    const abort = new AbortController();
    replacement.current = abort;
    try {
      const uploaded = await uploadFile(
        model.room.id,
        pending,
        abort.signal,
        model.room.platform === "cloudflare",
      );
      if (!uploaded.image) throw new Error("Image dimensions unavailable");
      abort.signal.throwIfAborted();
      const current = editor.getShape<TLImageShape>(s.id);
      if (
        !current ||
        current.isLocked ||
        editor.getIsReadonly() ||
        current.props.assetId !== s.props.assetId
      )
        throw new Error("The image changed while preparing its replacement.");
      const id = AssetRecordType.createId();
      editor.markHistoryStoppingPoint("Replace image");
      editor.createAssets([
        AssetRecordType.create({
          id,
          type: "image",
          props: {
            name: pending.name,
            src: uploaded.src,
            w: uploaded.image.w,
            h: uploaded.image.h,
            mimeType: pending.type,
            isAnimated: false,
          },
          meta: { imagePreviews: true },
        }),
      ]);
      const sourceRatio = uploaded.image.w / uploaded.image.h;
      const boxRatio = current.props.w / current.props.h;
      const crop =
        placement === "fit"
          ? null
          : sourceRatio > boxRatio
            ? {
                topLeft: { x: (1 - boxRatio / sourceRatio) / 2, y: 0 },
                bottomRight: { x: (1 + boxRatio / sourceRatio) / 2, y: 1 },
              }
            : {
                topLeft: { x: 0, y: (1 - sourceRatio / boxRatio) / 2 },
                bottomRight: { x: 1, y: (1 + sourceRatio / boxRatio) / 2 },
              };
      editor.updateShape({
        id: s.id,
        type: "image",
        props: {
          assetId: id,
          altText: pending.name,
          crop,
          ...(placement === "fit" ? { h: current.props.w / sourceRatio } : {}),
        },
        meta: { ...current.meta, scaleReview: true },
      });
      setPending(null);
      notify("Image replaced. Review scale and attached trace alignment.");
    } catch (error) {
      if (!abort.signal.aborted) notify((error as Error).message);
    } finally {
      replacement.current = null;
      setBusy(false);
    }
  };
  return (
    <div className="image-properties">
      <section className="image-properties-section">
        <h4>Appearance</h4>
        <OpacityControl model={model} shape={s} label="Image opacity" />
        <label>
          <input
            type="checkbox"
            checked={!!paperRemoval(s.meta.paperRemoval)}
            onChange={(e) => {
              editor.markHistoryStoppingPoint("Remove white paper");
              editor.updateShape({
                id: s.id,
                type: "image",
                meta: { ...s.meta, paperRemoval: e.target.checked ? 0.12 : 0 },
              });
            }}
          />
          Remove white paper
        </label>
        {!!paperRemoval(s.meta.paperRemoval) && (
          <>
            <label>
              Paper removal
              <input
                aria-label="Paper removal strength"
                type="range"
                min=".02"
                max=".5"
                step=".01"
                value={paperRemoval(s.meta.paperRemoval)}
                onChange={(e) =>
                  editor.updateShape({
                    id: s.id,
                    type: "image",
                    meta: { ...s.meta, paperRemoval: Number(e.target.value) },
                  })
                }
              />
            </label>
            <small>For drawings on white paper; pale details may fade.</small>
          </>
        )}
      </section>
      <section className="image-properties-section">
        <h4>Geometry</h4>
        <details>
          <summary>Size & orientation</summary>
          <label>
            Width
            <input
              aria-label="Image width"
              type="number"
              min={10}
              max={10000}
              value={Math.round(s.props.w)}
              onChange={(e) => {
                const w = Math.max(10, Math.min(10000, Number(e.target.value)));
                update({ w, h: (w * s.props.h) / s.props.w });
              }}
            />
          </label>
          <label>
            Rotation
            <input
              aria-label="Image rotation"
              type="number"
              min={-360}
              max={360}
              value={Math.round((s.rotation * 180) / Math.PI)}
              onChange={(e) =>
                editor.updateShape({
                  id: s.id,
                  type: "image",
                  rotation: ((Number(e.target.value) || 0) * Math.PI) / 180,
                })
              }
            />
          </label>
          <div className="format-buttons">
            <button onClick={() => update({ flipX: !s.props.flipX })}>
              Flip horizontal
            </button>
            <button onClick={() => update({ flipY: !s.props.flipY })}>
              Flip vertical
            </button>
          </div>
        </details>
        <button
          onClick={() => {
            const asset = s.props.assetId && editor.getAsset(s.props.assetId);
            if (asset?.type === "image")
              editor.updateShape({
                id: s.id,
                type: "image",
                props: {
                  h: (s.props.w * asset.props.h) / asset.props.w,
                  crop: null,
                },
              });
          }}
        >
          Fit full image
        </button>
      </section>
      <section className="image-properties-section">
        <h4>Border & mask</h4>
        <details>
          <summary>Options</summary>
          <label>
            Mask
            <select
              aria-label="Image mask"
              value={String(s.meta.mask || "none")}
              onChange={(e) => style("mask", e.target.value)}
            >
              <option value="none">Rectangle</option>
              <option value="round">Rounded corners</option>
              <option value="ellipse">Ellipse</option>
            </select>
          </label>
          <label>
            Border width
            <input
              aria-label="Image border width"
              type="number"
              min={0}
              max={20}
              value={Number(s.meta.borderWidth) || 0}
              onChange={(e) =>
                style(
                  "borderWidth",
                  Math.max(0, Math.min(20, Number(e.target.value))),
                )
              }
            />
          </label>
          <label>
            Border color
            <input
              aria-label="Image border color"
              type="color"
              value={String(s.meta.borderColor || "#25343b")}
              onChange={(e) => style("borderColor", e.target.value)}
            />
          </label>
        </details>
      </section>
      <section className="image-properties-section">
        <h4>Source</h4>
        <button
          onClick={() => {
            const id = newTracingSheet(editor, s);
            if (id) model.annotation.begin(id, true);
          }}
        >
          Lay tracing paper over image
        </button>
        {asset?.meta.imageFormat && (
          <small>
            {String(asset.meta.imageFormat)} ·{" "}
            {asset.meta.imageAnimation === "poster"
              ? "static board poster; download the original for animation"
              : "converted preview; original retained"}
          </small>
        )}
        <label>
          Description
          <input
            aria-label="Image description"
            maxLength={500}
            value={s.props.altText}
            onChange={(e) => update({ altText: e.target.value })}
          />
        </label>
        <label className="replace-image button-like">
          Choose replacement image
          <input
            aria-label="Choose replacement image"
            type="file"
            accept="image/png,image/jpeg,image/webp"
            disabled={busy}
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = "";
              if (!file) return;
              if (
                !file.size ||
                file.size >
                  (model.room.platform === "cloudflare"
                    ? 20_000_000
                    : 30_000_000)
              ) {
                notify("Choose a nonempty image within the upload limit.");
                return;
              }
              if (
                !["image/png", "image/jpeg", "image/webp"].includes(file.type)
              ) {
                notify("Choose a PNG, JPEG or WebP image.");
                return;
              }
              setPending(file);
            }}
          />
        </label>
        {pending && preview && (
          <div
            className="image-replacement-review"
            role="group"
            aria-label="Review image replacement"
          >
            <strong>Review replacement</strong>
            <small>
              {pending.name} · original image stays available through Undo
            </small>
            <div
              className="replacement-preview-box"
              style={{ aspectRatio: `${s.props.w} / ${s.props.h}` }}
            >
              <img
                src={preview}
                alt="Replacement preview"
                style={{
                  objectFit: placement === "fill" ? "cover" : "contain",
                }}
              />
            </div>
            <div className="format-buttons">
              <button
                type="button"
                aria-pressed={placement === "fit"}
                onClick={() => setPlacement("fit")}
              >
                Fit full image
              </button>
              <button
                type="button"
                aria-pressed={placement === "fill"}
                onClick={() => setPlacement("fill")}
              >
                Fill layout box
              </button>
            </div>
            <small>
              {placement === "fit"
                ? "Shows the full image and changes its height."
                : "Fills the current box and crops the excess."}
            </small>
            <div className="format-buttons">
              <button
                type="button"
                disabled={busy}
                onClick={() => setPending(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="primary"
                disabled={busy}
                onClick={() => void applyReplacement()}
              >
                {busy ? "Replacing…" : "Apply replacement"}
              </button>
            </div>
          </div>
        )}
        {s.meta.scaleReview && (
          <small>Source replaced · review scale and trace alignment.</small>
        )}
      </section>
    </div>
  );
}
